import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import PropertyCard from '../components/PropertyCard';
import LoadingSpinner from '../components/LoadingSpinner';
import API_URL from '../utils/api';

const ChoiceCategoryProperties = () => {
  const { category: categorySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const propertyRefs = useRef({});
  const hasRestoredRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Map slugs to actual category names
  const categoryMap = {
    'agricultural-land': 'Agricultural Land',
    'independent-house': 'Independent House',
    'open-plot': 'Open Plot',
    'apartment': 'Apartment',
    'farmhouse': 'Farmhouse',
    'office-commercial-space': 'Office / Commercial Space'
  };

  const categoryAliases = {
    'Office / Commercial Space': ['Office / Commercial Space', 'Office Space']
  };

  const doesCategoryMatch = (propertyCategory, expectedCategory) => {
    if (!propertyCategory || !expectedCategory) return false;
    if (propertyCategory === expectedCategory) return true;
    return categoryAliases[expectedCategory]?.includes(propertyCategory);
  };

  const category = categoryMap[categorySlug] || categorySlug;

  // Get ref for a specific property card
  const getPropertyRef = useCallback((propertyId) => {
    if (!propertyRefs.current[propertyId]) {
      propertyRefs.current[propertyId] = React.createRef();
    }
    return propertyRefs.current[propertyId];
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [categorySlug]);

  // Restore scroll and highlight card when returning from detail page
  useEffect(() => {
    if (hasRestoredRef.current || loading || properties.length === 0) return;

    let shouldRestore = false;
    let clickedPropertyId = null;

    // 1. Check location.state (React Router navigation)
    if (location.state?.restoreContext && location.state?.clickedPropertyId) {
      shouldRestore = true;
      clickedPropertyId = location.state.clickedPropertyId;
    }

    // 2. Check sessionStorage restoreScrollId set by PropertyDetails handleBackClick
    if (!shouldRestore) {
      const savedId = sessionStorage.getItem('restoreScrollId');
      if (savedId && !savedId.startsWith('cat-') && !savedId.startsWith('choice-') && !savedId.startsWith('section-')) {
        shouldRestore = true;
        clickedPropertyId = savedId;
        sessionStorage.removeItem('restoreScrollId');
        sessionStorage.removeItem('restoreScrollSection');
      }
    }

    // 3. Check navigationContext as fallback
    if (!shouldRestore) {
      const storedContext = sessionStorage.getItem('navigationContext');
      if (storedContext) {
        try {
          const context = JSON.parse(storedContext);
          if (Date.now() - context.timestamp < 15000 && context.restoreContext) {
            shouldRestore = true;
            clickedPropertyId = context.clickedPropertyId;
          }
          sessionStorage.removeItem('navigationContext');
        } catch (e) {}
      }
    }

    if (shouldRestore && clickedPropertyId) {
      const attemptRestore = (attempts = 0) => {
        if (attempts > 25) {
          hasRestoredRef.current = true;
          return;
        }
        const cardRef = propertyRefs.current[clickedPropertyId];
        if (cardRef?.current) {
          scrollTimeoutRef.current = setTimeout(() => {
            cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            cardRef.current.classList.add('card-highlight');
            setTimeout(() => cardRef.current?.classList.remove('card-highlight'), 2000);
          }, 150);
          hasRestoredRef.current = true;
          sessionStorage.removeItem('lastClickedProperty');
        } else {
          setTimeout(() => attemptRestore(attempts + 1), 80);
        }
      };
      attemptRestore();
    }

    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [loading, location.state, properties.length]);

  // Handle system back button (browser back pressed while on this page)
  useEffect(() => {
    const handlePopState = () => {
      // Always write the correct restoreScrollId so Home scrolls to the right choice tile,
      // regardless of whether the user came via the in-page Back button or the browser button.
      try {
        sessionStorage.setItem('restoreScrollId', `choice-${categorySlug}`);
        sessionStorage.setItem('restoreScrollSection', 'choice');
      } catch (_) {}

      const storedContext = sessionStorage.getItem('navigationContext');
      if (storedContext) {
        try {
          const context = JSON.parse(storedContext);
          if (Date.now() - context.timestamp < 10000 && context.restoreContext) {
            // Refresh the navigationContext so Home knows to restore scroll
            sessionStorage.setItem('navigationContext', JSON.stringify({
              restoreContext: true,
              clickedPropertyId: context.clickedPropertyId,
              section: 'choice-properties',
              isChoice: true,
              timestamp: Date.now()
            }));
          }
        } catch (e) {
          console.error('Error handling system back button:', e);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [categorySlug]);

  const fetchProperties = async () => {
    try {
      const res = await fetch(`${API_URL}/api/properties`);
      const data = await res.json();
      const filtered = data.filter(p => {
        const sections = p.sections || [p.section];
        const categoryMatch = doesCategoryMatch(p.category, category) || doesCategoryMatch(p.type, category);
        return categoryMatch && p.status === 'available' && sections.includes('choice');
      });
      setProperties(filtered);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    // Save scroll target so Home scrolls to the choice tile
    sessionStorage.setItem('restoreScrollId', `choice-${categorySlug}`);
    sessionStorage.setItem('restoreScrollSection', 'choice');
    sessionStorage.setItem('homeRestoreId', `choice-${categorySlug}`);
    sessionStorage.setItem('homeRestoreSection', 'choice');
    sessionStorage.setItem('pendingListingRoute', `${location.pathname}${location.search}`);
    navigate('/');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16 pb-12">
      {/* Sticky Back Bar — matches PropertyDetails style */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-gray-600 hover:text-primary font-medium transition-colors group"
          >
            <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
            <span>Back</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-gray-800">{category}</h1>
        
        {properties.length === 0 ? (
          <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
            <div className="max-w-md mx-auto bg-white rounded-xl shadow-2xl p-12 text-center">
              <div className="text-primary mb-6">
                <svg className="w-32 h-32 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h2 className="text-gray-800 text-2xl font-bold mb-3">No Properties Available</h2>
              <p className="text-gray-600 text-base mb-6">
                We currently don't have any properties in this category. Please check back later or explore other categories.
              </p>
              <button
                onClick={handleBackClick}
                className="bg-primary text-white px-8 py-3 rounded-lg hover:opacity-90 transition font-semibold"
              >
                Back to Home
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <div
                key={property._id}
                ref={getPropertyRef(property._id)}
              >
                <PropertyCard 
                  property={property} 
                  section="choice"
                  fromCategory={categorySlug}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChoiceCategoryProperties;
